import * as React from "react";
import {cx}       from "emotion";

import "./photo-dashboard.scss";

import {consumeContext}      from "@Common/react/context";
import {ComponentLink,
        Link, makeChain}     from "@Common/react/cst2";
import {fragments}           from "@Common/react/fragments";
import {Invoke}              from "@Common/react/invoke";
import {SessionStorageState} from "@Common/react/session-storage-state";
import {scnv}                from "@Common/react/state-change-nested-value";
import {UseRef}              from "@Common/react/use-ref";
import {UseState}            from "@Common/react/use-state";
import {Types}               from "@Common/types";
import {isDevXd}             from "@Common/ui-components/debug/is-dev";
import {ModalService3}       from "@Common/ui-components/modal-service-3/modal-service-3";

import {GalleryTabs}               from "./components/gallery-tabs";
import {NoticeMessage}             from "./components/notice-message";
import {TabBrandLogos}             from "./tabs/tab-brand-logos";
import {TabCustomersCanvasGallery} from "./tabs/tab-customers-canvas-gallery";
import {TabDesignations}           from "./tabs/tab-designations";
import {TabHeadshots}              from "./tabs/tab-headshots";
import {TabListings}               from "./tabs/tab-listings";
import {TabLogos}                  from "./tabs/tab-logos";
import {TabOrderGallery}           from "./tabs/tab-order-gallery";
import {TabPhotoIntegrations}      from "./tabs/tab-photo-integrations";
import {TabPreviousOrders}         from "./tabs/tab-previous-orders";
import {TabPropertyPhotos}         from "./tabs/tab-property-photos";
import {TabQrCodes}                from "./tabs/tab-qr-codes";
import {IsMobile}                  from "./utils/is-mobile";


/*  Modal for selecting images from various galleries and applying to an order.
    For .show() parameters, see PhotoDashboardData in /ui/common/types/common/ui-components/photo-dashboard.d.ts
*/
export const PhotoDashboard = ({
    next: rootNext,
}:{
    next : ComponentLink<typeof ModalService3<{}>, "rootNext">,
}) => (

    makeChain<JSX.Element>()
    // TODO: Evaluate if the .show() function displays when using this component, and that the PhotoDashboardData
    // properties display and are validated in the .show() call - if this doesn't work, then try swapping the above
    // makeChain with the below - This worked in a preliminary test, but it may cause other issues - Not sure until
    // calling components are converted to use typed CS
    // makeChain<ComponentNext<typeof ModalService3<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>, "rootNext">>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("systemConstants"))
    .then(...consumeContext("company"))
    .then(...consumeContext("branch"))

    // check for mobile
    .then("isMobile", (_, next:Link<JSX.Element, boolean>) => IsMobile({next}))

    // set modal attributes
    .then("modal", ({isMobile}, next:ComponentLink<typeof ModalService3<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>>) => ModalService3({
        next,
        rootNext,
        width: "fit-content",
        contentClassName: "gallery-a3d",
        xdQaName: "Gallery",
        top: isMobile ? 15 : 60,
        bottom: 0,
        preventBackgroundScroll: true,
    }))

    // set state vars
    .then("state", ({modal}, next:ComponentLink<typeof UseState<{
        processType        : Types.Common.UiComponents.PhotoDashboard.processTypes | "",
        activeTab          : string, // TODO: Limit to string values related to tabDisplayNames
        surfaceData       ?: Types.Common.Tools.CcIntegration.Models.ProductSurfaceWithImages[],
        multiSelectImages  : Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectImage[],
    }>>) => UseState({
        defaultValue: {
            processType: (modal.params.onApplyImage) ? "single" : (modal.params.onApplyImages) ? "multi" : (modal.params.onApplyImport) ? "import" : "",
            activeTab: "",
            multiSelectImages: modal.params.selectedImages,
        },
        next,
    }))

    // set persisted state vars
    .then("statePersisted", (_, next:ComponentLink<typeof UseState<{
        viewType           : Types.Common.UiComponents.PhotoDashboard.viewTypes,
        numResultsPerPage  : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    }>>) => SessionStorageState({
        storageKey: "photoDashboardSelections",
        defaultData: {
            viewType: "large",
            numResultsPerPage: 10,
        },
        next,
    }))

    // load surface data from the editor - will be used to construct the import preview drop zone
    .then(({state, modal: {params: {onLoadSurfaceData}}}, next) => fragments(
        next(),
        onLoadSurfaceData && state.value.activeTab && !state.value.surfaceData && Invoke({
            action: async () => {
                const surfaceData = await onLoadSurfaceData();
                scnv(state, ["surfaceData"], () => surfaceData);
            },
        })
    ))

    // build list of available tabs
    .then("tabs", ({state, modal, company, branch}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Models.Tab[]>) => {

        let tabs = [];

        // set tabs for single image image import
        if (["multi", "single"].includes(state.value.processType)) {

            if (modal.params.imageArea?.allowedTabs) {

                // go through each template gallery type to check if a known type and set to use the display name
                modal.params.imageArea.allowedTabs.forEach((tabName) => {

                    // convert CC template tab names to standard names
                    switch(tabName) {
                        case "Image Gallery":
                            tabName = PhotoDashboardTabs.orderGallery;
                            break;
                        case "Property Photos":
                        case "Property Upload Gallery": // alternate for property photos - used in universal template
                            tabName = PhotoDashboardTabs.propertyPhotos;
                            break;
                        case "Agent Logo Gallery":
                        case "Logo Upload Gallery": // alternate for agent logo gallery - used in universal template
                            tabName = PhotoDashboardTabs.logos;
                            break;
                        case "Brand Logo Gallery":
                            tabName = PhotoDashboardTabs.brandLogos;
                            break;
                        case "Headshot Gallery":
                        case "Headshot Upload Gallery": // alternate for headshot gallery - used in universal template
                            tabName = PhotoDashboardTabs.headshots;
                            break;
                        case "Designation Gallery":
                            tabName = PhotoDashboardTabs.designations;
                            break;
                        case "Photo Services":
                            tabName = "Photo Integrations";
                            break;
                    }

                    // show the new badge if any new photo integrations at company or branch level
                    const isNew = (
                        tabName === "Photo Integrations"
                        && (
                            company?.imageGallery?.hasNewIntegrations
                            || branch?.imageGallery?.hasNewIntegrations
                        )
                    );

                    // add the tab if not already included
                    if (tabName && !tabs.find(el => el.title === tabName)) {
                        tabs.push({
                            title: tabName,
                            isNew
                        });
                    }

                });

            }

            // if photo album is available and not already the first tab, move to the first
            const photoAlbumIndex = tabs.findIndex(el => el.title == PhotoDashboardTabs.orderGallery);
            if (photoAlbumIndex >= 1) {
                tabs.unshift(tabs.splice(photoAlbumIndex, 1)[0]);
            }

        }

        // set tabs for multi-select import
        else if (state.value.processType === "import") {
            tabs.push({title: PhotoDashboardTabs.previousOrders});
        }

        // select the first tab if none already set or the selected tab is no longer available
        if (tabs.length && (!state.value.activeTab || !tabs.find(x => x.title === state.value.activeTab))) {
            return Invoke({
                fn: () => state.onChange({
                    ...state.value,
                    activeTab: tabs[0].title,
                })
            });
        }

        return next(tabs);

    })

    // create the drag image ref hook
    .then("dragImageRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // set options for single-select mode
    .then("singleSelectOptions", ({state, modal}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions>) => next(
        (state.value.processType !== "single") ? null : {
            onApply: modal.params.onApplyImage,
        }
    ))

    // set options for multi-select mode
    .then("multiSelectOptions", ({state, modal}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions>) => next(
        (state.value.processType !== "multi") ? null : {
            maxSelections: modal.params.maxImageSelections,
            selectedImages: state.value.multiSelectImages,
            onApply: modal.params.onApplyImages,
            onSelect: (image) => {

                let multiSelectImages = state.value.multiSelectImages;

                // remove the image if already selected - checking against original image ID if de-selecting from tab that the image was originally sourced from
                const existingIndex = multiSelectImages.findIndex(x => x.assetType === image.assetType
                    && (x.imageId === image.imageId
                        || (x.originalImageId && x.originalImageId === image.originalImageId)
                    )
                );
                if (existingIndex >= 0) multiSelectImages.splice(existingIndex, 1);

                // return if already at max
                else if (multiSelectImages.length >= modal.params.maxImageSelections) return;

                // add the image if not selected
                else multiSelectImages.push(image);

                // update the state with adjusted image selections
                state.onChange({...state.value, multiSelectImages});

            },
            onClearSelections: () => {
                state.onChange({...state.value, multiSelectImages: []});
            },
        }
    ))

    // set options for import mode
    .then("importOptions", ({state, modal, dragImageRef, apis}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Models.ImportOptions>) => next(
        (state.value.processType !== "import") ? null : {
            onImport: async () => {
                if (isDevXd()) console.log("PhotoDashboard.onImport", state.value.surfaceData);

                // load all current order images
                const currentOrderImages = await apis.next.gallery.orders.getImages(modal.params.orderId);
                let currentOrderImageIds = currentOrderImages.map(image => image.id);
                let copiedImages = [];

                // loop through all image areas on each surface and copy any images not already in the order gallery
                // to the current order gallery
                let surfaceData = state.value.surfaceData;
                for (const surface of surfaceData) {
                    for (const item of surface.items) {

                        if (item.image?.imageId && !currentOrderImageIds.includes(item.image.imageId)) {

                            // use a previously-copied image if found
                            if (copiedImages["image"+item.image.imageId]) {

                                const copiedImage = copiedImages["image"+item.image.imageId];
                                if (isDevXd()) console.log("PhotoDashboard.onImport changing selected image", item.image.imageId, "for previously copied image", copiedImage.id);

                                item.image.imageId = copiedImage.id;
                                item.image.httpPath = copiedImage.thumbnailUrl;
                                item.image.httpPathPrint = copiedImage.printUrl;
                                item.image.width = copiedImage.width;
                                item.image.height = copiedImage.height;
                                continue;

                            }

                            // copy the image if not already copied
                            const savedImage = await apis.next.gallery.orders.copy({
                                orderId: modal.params.orderId,
                                imageSource: "order",
                                imageId: item.image.imageId
                            });
                            if (savedImage.errorMessage) continue;

                            if (isDevXd()) console.log("PhotoDashboard.onImport copied selected image", item.image.imageId, "to new order image", savedImage.id);

                            // add the image and ID to control lists
                            copiedImages["image"+item.image.imageId] = savedImage;
                            currentOrderImageIds.push(savedImage.id);

                            // update the selected image with the copied version
                            item.image.imageId = savedImage.id;
                            item.image.httpPath = savedImage.thumbnailUrl;
                            item.image.httpPathPrint = savedImage.printUrl;
                            item.image.width = savedImage.width;
                            item.image.height = savedImage.height;

                        }

                    }
                }

                // apply the import
                const errorMessage = await modal.params.onApplyImport(surfaceData);

                /* TODO: Display error message if provided - When adding this, also add error messaging from applyImageToImageField in cc-editor-2
                // set an error message if encountered
                if (errorMessage) {
                    return;
                }
                */

                // close the modal
                state.onChange();
                modal.close();
            },
            onApplyImage: (surfaceName, itemName, imageId = 0, thumbnailUrl = "", printUrl = "", imageWidth = 0, imageHeight = 0) => {
                if (isDevXd()) console.log("PhotoDashboard.onApplyImage", {surfaceName, itemName, imageId, thumbnailUrl, printUrl, imageWidth, imageHeight});

                let surfaceData = state.value.surfaceData;

                // replace the selected image with provided, or null if none provided
                surfaceData.find(x => x.name === surfaceName).items.find(x => x.name === itemName).image = (thumbnailUrl) ? {
                    ccStorageId: "",
                    httpPath: thumbnailUrl,
                    width: imageWidth,
                    height: imageHeight,
                    imageId,
                    httpPathPrint: printUrl,
                } : null;

                state.onChange({...state.value, surfaceData});

            },
            dragImageRef,
        }
    ))

    // create method to change pagination
    .then("onPaginateNumResults", ({statePersisted}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults>) => next(numResultsPerPage => {

        statePersisted.onChange({...statePersisted.value, numResultsPerPage});

    }))

    // render the modal contents
    .then(({state, statePersisted, modal, tabs, isMobile, onPaginateNumResults, singleSelectOptions, multiSelectOptions, importOptions, systemConstants}) => <>
        <div className="header">
            <div className="title">
                <span>Photo Dashboard</span>
                <small>Select the photo{["import", "multi"].includes(state.value.processType) && "s"} you want to use</small>
            </div>
            <div className="size-controls">
                <button className={cx({active: (statePersisted.value.viewType === "condensed")})}
                        xd-qa-type="button" xd-qa-name="ccgallery viewtype condensed"
                        onClick={() => statePersisted.onChange({...statePersisted.value, viewType: "condensed"})}
                >
                    <i className="fa fa-th"/>
                </button>
                <div className="separator"/>
                <button className={cx({active: (statePersisted.value.viewType === "large")})}
                        xd-qa-type="button" xd-qa-name="ccgallery viewtype large"
                        onClick={() => statePersisted.onChange({...statePersisted.value, viewType: "large"})}
                >
                    <i className="fa fa-th-large"/>
                </button>
            </div>
        </div>
        <div className="sections" xd-qa-type="modal" xd-qa-name="ccgallery modal">
            {/* temporarily only displaying tabs for single image import - only previous orders is currently supported */}
            {["single", "multi"].includes(state.value.processType) && (
            <div className="header">
                <GalleryTabs
                    tabs={tabs}
                    activeTabTitle={state.value.activeTab}
                    onChangeTab={(tabTitle) => {
                        state.onChange({...state.value, activeTab: tabTitle});
                    }}
                />
            </div>
            )}
            {modal.params.adminTool ? (
                (state.value.activeTab === PhotoDashboardTabs.propertyPhotos) ? (
                    <TabPropertyPhotos
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        orderId={0}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.previousOrders) ? (
                    <TabPreviousOrders
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        importOptions={importOptions}
                        isMobile={isMobile}
                        orderId={0}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        surfaceData={state.value.surfaceData}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.photoIntegrations) ? (
                    <TabPhotoIntegrations
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        orderId={0}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.designations) ? (
                    <TabDesignations
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        useEpsDesignations={modal.params.useEpsDesignations}
                        templateId={modal.params.templateId}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.brandLogos) ? (
                    <TabBrandLogos
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab) ? (
                    <TabCustomersCanvasGallery
                        modal={modal}
                        folderName={state.value.activeTab}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : <NoticeMessage text="There are currently no galleries available."/>
            ) : (
                (state.value.activeTab === PhotoDashboardTabs.orderGallery) ? (
                    <TabOrderGallery
                        modal={modal}
                        orderId={modal.params.orderId}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.propertyPhotos) ? (
                    <TabPropertyPhotos
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        orderId={modal.params.orderId}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.previousOrders) ? (
                    <TabPreviousOrders
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        importOptions={importOptions}
                        isMobile={isMobile}
                        orderId={modal.params.orderId}
                        surfaceData={state.value.surfaceData}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.photoIntegrations) ? (
                    <TabPhotoIntegrations
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        orderId={modal.params.orderId}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.headshots) ? (
                    <TabHeadshots
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        allowUploadOverride={modal.params.allowUploadOverride}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.designations) ? (
                    <TabDesignations
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        useEpsDesignations={modal.params.useEpsDesignations}
                        templateId={modal.params.templateId}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.listings) ? (
                    <TabListings
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        orderId={modal.params.orderId}
                        listing={modal.params.listing}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.brandLogos) ? (
                    <TabBrandLogos
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.logos) ? (
                    <TabLogos
                        modal={modal}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        allowUploadOverride={modal.params.allowUploadOverride}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : (state.value.activeTab === PhotoDashboardTabs.qrCodes) ? (
                    <TabQrCodes
                        modal={modal}
                        orderId={modal.params.orderId}
                        viewType={statePersisted.value.viewType}
                        numResultsPerPage={statePersisted.value.numResultsPerPage}
                        imageArea={modal.params.imageArea}
                        singleSelectOptions={singleSelectOptions}
                        multiSelectOptions={multiSelectOptions}
                        isMobile={isMobile}
                        disallowSvgImages={modal.params.disallowSvgImages}
                        onPaginateNumResults={onPaginateNumResults}
                    />
                ) : <NoticeMessage text="There are currently no galleries available."/>
            )}
            {!!importOptions && (
                <div className="import-drag-image" ref={ref => importOptions.dragImageRef.set(ref)}></div>
            )}
        </div>
    </>)

    .run()

);

export const PhotoDashboardTabs = {
    brandLogos:        "Brand Logo Gallery",
    designations:      "Designation Gallery",
    headshots:         "Headshot Gallery",
    listings:          "Listing Gallery",
    logos:             "Logo Gallery",
    orderGallery:      "Photo Album",
    photoIntegrations: "Photo Integrations",
    previousOrders:    "Previous Orders",
    propertyPhotos:    "Company Gallery",
    qrCodes:           "QR Code Gallery",
};
